<template>
    <div id="app" class="site container hfeed">
        <HeadNav selectedLanguage="en" page="tutorials-customization" />

        <div class="markdown-content">
            <h1>GB WhatsApp Customization Guide – Complete Tutorial</h1>

            <p>Personalize your GB WhatsApp experience with our comprehensive customization guide. Learn how to transform your messaging app into a unique expression of your style and preferences.</p>

            <h2>Introduction to Customization</h2>

            <p>One of the biggest advantages of GB WhatsApp over the official app is its extensive customization options. This guide covers everything from themes to functional layouts, helping you create the perfect messaging experience.</p>

            <h2>Theme Changes</h2>

            <p>Transform the look of your app with themes:</p>
            <ul>
                <li><strong>Built-in Themes:</strong> Choose from dozens of pre-installed themes</li>
                <li><strong>Download Themes:</strong> Find and download community-created themes</li>
                <li><strong>Theme Creator:</strong> Design your own custom themes</li>
                <li><strong>Theme Settings:</strong> Customize individual theme elements</li>
                <li><strong>Theme Updates:</strong> Keep your themes up to date</li>
            </ul>

            <h2>Font Settings</h2>

            <p>Change how your text looks:</p>
            <ul>
                <li><strong>Built-in Fonts:</strong> Select from various font styles</li>
                <li><strong>Font Size:</strong> Adjust text size for readability</li>
                <li><strong>Custom Fonts:</strong> Install your own font files</li>
                <li><strong>Chat Fonts:</strong> Different fonts for different chats</li>
                <li><strong>Font Color:</strong> Customize font colors</li>
            </ul>

            <h2>Chat Bubbles</h2>

            <p>Customize your conversation bubbles:</p>
            <ul>
                <li><strong>Bubble Styles:</strong> Choose different bubble shapes</li>
                <li><strong>Custom Colors:</strong> Set custom colors for chat bubbles</li>
                <li><strong>Bubble Borders:</strong> Add borders to your bubbles</li>
                <li><strong>Transparency:</strong> Adjust bubble transparency</li>
                <li><strong>Sender Bubbles:</strong> Different styles for sent and received messages</li>
            </ul>

            <h2>Notification Settings</h2>

            <p>Personalize your notifications:</p>
            <ul>
                <li><strong>Custom Sounds:</strong> Set unique notification sounds</li>
                <li><strong>LED Colors:</strong> Choose LED notification colors</li>
                <li><strong>Vibration Patterns:</strong> Customize vibration alerts</li>
                <li><strong>Popup Notifications:</strong> Configure popup style notifications</li>
                <li><strong>Contact-Specific:</strong> Different notifications per contact</li>
            </ul>

            <h2>Background Changes</h2>

            <p>Set custom chat wallpapers:</p>
            <ul>
                <li><strong>Built-in Wallpapers:</strong> Choose from pre-installed backgrounds</li>
                <li><strong>Gallery Images:</strong> Use your own photos as wallpapers</li>
                <li><strong>Wallpaper per Chat:</strong> Different wallpapers for different chats</li>
                <li><strong>Solid Colors:</strong> Use solid color backgrounds</li>
                <li><strong>Live Wallpapers:</strong> Animated wallpaper support</li>
            </ul>

            <h2>Shortcuts and Widgets</h2>

            <p>Quick access to your favorite features:</p>
            <ul>
                <li><strong>Home Screen Shortcuts:</strong> Quick launch GB WhatsApp features</li>
                <li><strong>Widget Configuration:</strong> Set up home screen widgets</li>
                <li><strong>Shortcut Customization:</strong> Customize shortcut icons</li>
                <li><strong>Quick Actions:</strong> Configure quick action shortcuts</li>
                <li><strong>Launcher Integration:</strong> Integrate with Android launchers</li>
            </ul>

            <h2>Feature Layout</h2>

            <p>Arrange app features your way:</p>
            <ul>
                <li><strong>Menu Layout:</strong> Customize the main menu appearance</li>
                <li><strong>Tab Arrangement:</strong> Reorder tabs and sections</li>
                <li><strong>Hide Features:</strong> Hide unwanted features from menus</li>
                <li><strong>Feature Icons:</strong> Customize app icons</li>
                <li><strong>Status Layout:</strong> Change how status updates appear</li>
            </ul>

            <h2>Customization Showcase</h2>

            <p>Get inspired by popular customization combinations:</p>
            <ul>
                <li><strong>Minimalist:</strong> Clean, simple interface</li>
                <li><strong>Dark Mode:</strong> Eye-friendly dark theme</li>
                <li><strong>Colorful:</strong> Vibrant, colorful themes</li>
                <li><strong>Professional:</strong> Clean, business-like appearance</li>
                <li><strong>Custom:</strong> Unique combination of elements</li>
            </ul>

            <h2>Best Practices</h2>

            <p>Tips for effective customization:</p>
            <ul>
                <li><strong>Performance Impact:</strong> Heavy customization may affect performance</li>
                <li><strong>Theme Sources:</strong> Only download themes from trusted sources</li>
                <li><strong>Backup Settings:</strong> Note down your customizations</li>
                <li><strong>Regular Updates:</strong> Update themes and customizations regularly</li>
                <li><strong>Readability:</strong> Ensure text remains readable</li>
            </ul>

            <h2>Troubleshooting</h2>

            <p>Solve common customization issues:</p>
            <ul>
                <li><strong>Theme Not Applying:</strong> Clear app cache and retry</li>
                <li><strong>Font Not Working:</strong> Ensure font file format is supported</li>
                <li><strong>Wallpaper Issues:</strong> Check image resolution and format</li>
                <li><strong>Layout Problems:</strong> Reset to default and reconfigure</li>
                <li><strong>Performance Issues:</strong> Reduce customization complexity</li>
            </ul>

            <h2>Related Topics</h2>

            <p>Explore more related guides:</p>
            <ul>
                <li><a href="/features/overview/">GB WhatsApp Features Overview</a> - All available features</li>
                <li><a href="/features/media/">Media Sharing Features</a> - Customize media sharing</li>
            </ul>
        </div>
    </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';

export default {
    name: 'TutorialsCustomization',
    components: { HeadNav },
};
</script>

<style scoped>
.markdown-content {
    padding: 20px;
    line-height: 1.6;
    margin: 0 auto;
    max-width: 1000px;
    background: #ffffff;
    border-radius: 8px;
}

.markdown-content h1 {
    font-size: 2em;
    margin: 0.67em 0;
    border-bottom: 2px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    text-align: center;
}

.markdown-content h2 {
    font-size: 1.5em;
    margin: 0.75em 0;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.3em;
    color: #2d3748;
    margin-top: 40px;
}

.markdown-content h3 {
    font-size: 1.25em;
    margin: 0.83em 0;
    color: #2d3748;
    margin-top: 30px;
}

.markdown-content p {
    margin: 1em 0;
}

.markdown-content ul,
.markdown-content ol {
    margin: 1em 0;
    padding-left: 2em;
}

.markdown-content li {
    margin: 0.5em 0;
}

.markdown-content img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 1em 0;
}

.markdown-content a {
    color: #667eea;
    text-decoration: none;
}

.markdown-content a:hover {
    text-decoration: underline;
}

.markdown-content strong {
    font-weight: 700;
}
</style>
